/**
 * Messages for the new console, scoped by role (Traci's review, 23 Sep 2026; v109 §8): Admin sees everyone with
 * Clients / Talent / Unread filters; Sales sees client conversations; Talent (coordinator) sees talent conversations.
 * Billing and Editing see only the conversations routed (tagged) to them. Anyone can tag the open conversation to a
 * department; that department gets a System notification. The inbox itself is the current Messages page.
 * Routing table: migration 20260925190000_message_routes.sql; until it has run, Billing and Editing see every
 * conversation their audience covers, and the page says so.
 * Traci's doc, Oct 2026: Billing and Editing also see the conversations their department started, and read/unread is
 * per person (message_reads, migration 20261013120000); until that has run, everyone shares one unread count.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertCircle, X } from 'lucide-react';
import Messages, { type PersonalMessages } from '@/pages/admin/Messages';
import { Button } from '@/components/admin/ui/button';
import { Input } from '@/components/admin/ui/input';
import { ROLE_LABELS } from '@/lib/staging/roleNav';
import { useStaging } from '../StagingLayout';
import { stagingFetch } from '../api';

import { MESSAGE_AUDIENCES as AUDIENCES, ROUTED_ONLY } from '@/lib/messages/conversations';

export const MESSAGE_AUDIENCES = AUDIENCES as Record<string, Array<'client' | 'talent'>>;
const DEPTS = ['billing', 'editing', 'sales', 'talent_coordinator', 'admin'];

type Route = { id: string; conversation_user_id: string; department: string; note: string | null; tagged_by_name: string | null; created_at: string; message_id?: string | null; message_excerpt?: string | null };
type Picked = { id: string; text: string; from: string; at: string; userId?: string } | null;

const routedAt = (iso: string) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Denver' });

function TagBar({ user, routes, onChange, role, picked: pickedAny, onUnpick, onGoTo }: { user: { id: string; name: string }; routes: Route[]; onChange: () => void; role?: string; picked?: Picked; onUnpick?: () => void; onGoTo?: (id: string) => void }) {
  // Only a message picked in this conversation
  const picked = pickedAny && pickedAny.userId === user.id ? pickedAny : null;
  const [dept, setDept] = useState('billing');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const mine = routes.filter((r) => r.conversation_user_id === user.id);
  const tag = async () => {
    setBusy(true);
    try {
      await stagingFetch('/api/admin/staging/messages/routes', { method: 'POST', body: JSON.stringify({ user_id: user.id, department: dept, note, message_id: picked?.id || undefined, excerpt: picked?.text || undefined }) });
      toast.success(`Tagged to ${ROLE_LABELS[dept as keyof typeof ROLE_LABELS] || dept}; they were notified`);
      setNote(''); onUnpick?.(); onChange();
    } catch (e: any) { toast.error(e.message || String(e)); } finally { setBusy(false); }
  };
  const clear = async (r: Route) => {
    try { await stagingFetch(`/api/admin/staging/messages/routes/${r.id}/clear`, { method: 'POST', body: '{}' }); onChange(); }
    catch (e: any) { toast.error(e.message || String(e)); }
  };
  return (
    <div className="rounded-lg border bg-card p-2 text-sm" aria-label="Route conversation">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">Routed to:</span>
        {mine.length === 0 && <span className="text-xs text-muted-foreground">nobody</span>}
        {mine.map((r) => (
          <span key={r.id} className="inline-flex items-center gap-1 rounded-full border border-primary/40 px-2 py-0.5 text-xs text-primary" title={[r.tagged_by_name, r.note].filter(Boolean).join(' · ')}>
            {ROLE_LABELS[r.department as keyof typeof ROLE_LABELS] || r.department}
            <button type="button" aria-label={`Remove ${r.department} tag`} onClick={() => clear(r)}><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      {/* Traci, 21 Oct 2026: whoever the conversation is routed to sees exactly what they are being asked, and since when */}
      {mine.length > 0 && (
        <ul className="mt-2 space-y-1" aria-label="Why this conversation was routed">
          {mine.map((r) => {
            const forMe = r.department === role;
            return (
              <li key={r.id} className={`flex items-start gap-2 rounded-md border px-2 py-1.5 text-xs ${forMe ? 'border-red-300 bg-red-50 text-red-900' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
                <AlertCircle className={`mt-0.5 h-3.5 w-3.5 flex-shrink-0 ${forMe ? 'text-red-600' : 'text-amber-600'}`} aria-hidden="true" />
                <span>
                  <b>{forMe ? 'For you' : `For ${ROLE_LABELS[r.department as keyof typeof ROLE_LABELS] || r.department}`}:</b> {r.note || 'No reason given'}
                  {r.message_excerpt && <span className="mt-0.5 block border-l-2 border-current/40 pl-2 italic">“{r.message_excerpt}”</span>}
                  <span className="block text-[11px] opacity-80">
                    Routed by {r.tagged_by_name || 'staff'} · {routedAt(r.created_at)}.{' '}
                    {r.message_id && onGoTo
                      ? <button type="button" className="font-semibold underline" onClick={() => onGoTo(r.message_id!)}>Go to message</button>
                      : 'Read from the messages around then.'}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {/* The message this routing is about, picked with "Route this" in the thread (Traci, 21 Oct 2026) */}
      {picked ? (
        <div className="mt-2 flex items-start gap-2 rounded-md border border-primary/40 bg-primary/5 px-2 py-1.5 text-xs">
          <span className="flex-1 min-w-0"><b>About this message</b> from {picked.from}: <span className="italic">“{picked.text.slice(0, 160)}{picked.text.length > 160 ? '…' : ''}”</span></span>
          <button type="button" aria-label="Remove the picked message" onClick={onUnpick}><X className="h-3 w-3" /></button>
        </div>
      ) : (
        <p className="mt-2 text-[11px] text-muted-foreground">Tip: highlight the part of a message that matters, then press “Route this” under it, so they read just that and can jump straight to it.</p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <select aria-label="Tag department" className="h-8 rounded-md border bg-background px-2 text-xs" value={dept} onChange={(e) => setDept(e.target.value)}>
          {DEPTS.map((d) => <option key={d} value={d}>{ROLE_LABELS[d as keyof typeof ROLE_LABELS] || d}</option>)}
        </select>
        <Input aria-label="Tag note" className="h-8 flex-1 text-xs" placeholder="Why (required): what should they answer?" value={note} onChange={(e) => setNote(e.target.value)} />
        <Button size="sm" className="h-8" disabled={busy || note.trim().length < 3 || mine.some((r) => r.department === dept)} onClick={tag}>Tag</Button>
      </div>
    </div>
  );
}

export default function MessagesPage() {
  const { role } = useStaging();
  const [routes, setRoutes] = useState<{ installed: boolean; routes: Route[] }>({ installed: false, routes: [] });
  const load = useCallback(() => stagingFetch('/api/admin/staging/messages/routes').then(setRoutes).catch(() => setRoutes({ installed: false, routes: [] })), []);
  useEffect(() => { load(); }, [load]);
  const tags = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const r of routes.routes) (out[r.conversation_user_id] ||= []).push(r.department);
    return out;
  }, [routes]);
  const routedOnly = ROUTED_ONLY.has(role) && routes.installed;
  // "Route this" on a message, and "Go to message" from a routing (Traci, 21 Oct 2026)
  const [picked, setPicked] = useState<Picked>(null);
  const [goTo, setGoTo] = useState<string | null>(null);
  // The flag on each routed message in the thread
  const markers = useMemo(() => {
    const out: Record<string, Array<{ label: string; why?: string | null }>> = {};
    for (const r of routes.routes) if (r.message_id) (out[r.message_id] ||= []).push({ label: `Routed to ${ROLE_LABELS[r.department as keyof typeof ROLE_LABELS] || r.department}`, why: r.note });
    return out;
  }, [routes]);
  const personal = useMemo<PersonalMessages>(() => ({
    load: () => stagingFetch(`/api/admin/staging/messages/mine?role=${encodeURIComponent(role)}`),
    markRead: (userId: string) => stagingFetch('/api/admin/staging/messages/read', { method: 'POST', body: JSON.stringify({ user_id: userId }) })
  }), [role]);
  return (
    <div>
      {ROUTED_ONLY.has(role) && !routes.installed && <p className="px-8 pt-6 text-sm text-amber-800">Routing needs the 20260925190000 migration; until then {ROLE_LABELS[role as keyof typeof ROLE_LABELS] || role} sees every conversation below.</p>}
      {routedOnly && <p className="px-8 pt-6 text-sm text-muted-foreground">Showing conversations routed to {ROLE_LABELS[role as keyof typeof ROLE_LABELS] || role} or started by {ROLE_LABELS[role as keyof typeof ROLE_LABELS] || role}.</p>}
      <Messages key={role} split audiences={MESSAGE_AUDIENCES[role] || ['client', 'talent']} routeTags={tags} routedTo={routedOnly ? role : null} personal={personal}
        paneHeader={routes.installed ? (u) => <TagBar user={u} routes={routes.routes} onChange={load} role={role} picked={picked} onUnpick={() => setPicked(null)} onGoTo={setGoTo} /> : undefined}
        chatProps={routes.installed ? { onPickMessage: (m) => { setPicked(m); setGoTo(null); }, highlightId: goTo, markers } : undefined} />
    </div>
  );
}
